import { Component, Watch, Vue } from "vue-property-decorator";
import DataTable, { TableHeader } from "@/components/data-table/DataTable";
import { Model } from "@/models/Model";
import { RoleEnum, User } from "@/models/User";
import ErrorHandler from "@/support/ErrorHandler";
import { Options } from "@/components/mi-dialog/MiDialog";
import { AxiosError } from "axios";
import CreateDialog from "@/views/Users/CreateDialog";
import CreateSupplierDialog from "@/views/Users/CreateSupplierDialog";
import { DateTime } from "luxon";
import { Lead } from "@/models/Lead";
import { Stylist } from "@/models/Stylist";
import { Admin } from "@/models/Admin";
import { Support } from "@/models/Support";
import { Contact } from "@/models/Contact";
import { Supplier } from "@/models/Supplier";
import { Questions } from '@/views/Questionnaire/questions';
import { languageOptions } from "@/support/Languages";
import UserStatus from "@/support/UserStatus";
import { LocaleMessage } from "vue-i18n";

@Component<Users>({
  components: {
    CreateDialog,
    CreateSupplierDialog
  }
})
export default class Users extends Vue {
  protected $pageTitle = "Users";
  private type: string | null = null;

  private questions = Questions;
  protected lastQuestion: Question | null = null;
  protected selected: any[] = [];
  protected selectedUser: User | null = null;

  protected statusOptions: any[] = [
    {
      text: "Draft",
      value: "0",
      type: "status"
    },
    {
      text: "New",
      value: "1",
      type: "status"
    },
    {
      text: "In progress",
      value: "2",
      type: "status"
    },
    {
      text: "Completed",
      value: "3",
      type: "status"
    }
  ];

  protected paymentStatusFilterOptions: any[] = [
    {
      text: "Fee betaald",
      value: "1",
      type: "has_paid_styling_fee"
    },
    {
      text: "Niet betaald",
      value: "0",
      type: "has_paid_styling_fee"
    },
    {
      text: "Toon alle leads",
      value: null,
      type: "has_paid_styling_fee"
    }
  ];

  protected addressCheckedFilterOptions: any[] = [
    {
      text: "Adres ok",
      value: "1",
      type: "address_checked"
    },
    {
      text: "Adres onzeker",
      value: "0",
      type: "address_checked"
    },
  ];

  protected selectedStatus: string = "";
  protected paymentSuccesFilterState: string | null = "1"; //true
  protected addressCheckedFilterState: string | null = null;
  protected filters: customFilter = {
    items: this.statusOptions
  };

  protected roleMap: object = {
    Admin: "administrator",
    Stylists: "stylist",
    Contacts: "contact",
    Support: "support"
  };

  protected isCreating: boolean = false;
  protected isCreatingSupplier: boolean = false;
  protected isShowingManyDialog: boolean = false;

  protected visibility: TableVisibility = {
    search: true,
    total: true,
    limit: true,
    pagination: true,
    checkboxes: false
  };

  @Watch("$route")
  public routeChanged(to: any, from: any) {
    if (to.meta.type !== from.meta.type) {
      this.type = to.meta.type;
      this.selectedStatus = "";
    }
  }

  public mounted() {
    // @ts-ignore
    this.type = this.$route.meta.type;
    this.$pageTitle = this.tableOptions.name.plural as string;
    this.$root.$emit("breadcrumbUpdated", {
      crumb: []
    });
  }

  public createPlayload(items: User[]): string[] {
    return items.map(item => {
      return `${item.id}`;
    });
  }

  get leadFilterObject() {
    return {
      status: this.selectedStatus,
      ...(typeof this.paymentSuccesFilterState === "string"
        ? {
          has_paid_styling_fee: this.paymentSuccesFilterState
        }
        : {}),
      ...(typeof this.addressCheckedFilterState === "string"
        ? {
          address_checked: this.addressCheckedFilterState
        }
        : {})
    };
  }

  get tableOptions() {
    const sourceMap: any = {
      Leads: {
        name: {
          singular: "Lead",
          plural: "Leads"
        },
        sort: {
          key: "created_at",
          order: "DESC"
        },
        headers: [
          {
            text: "Styling fee",
            value: "avatar",
            breakTitle: true,

            transform: (value: any, lead: Lead) => {
              if (lead.status === "Draft") {
                return;
              }

              if (lead.has_paid_styling_fee) {
                return `<i aria-hidden="true" class="v-icon material-icons theme--light view">check</i>`;
              } else {
                return ``;
              }
            },
            sortable: {
              key: "has_paid_styling_fee",
              order: "ASC"
            }
          },
          {
            text: "Naam",
            value: "name",
            action: "/leads/{id}",
            sortable: {
              key: "name",
              order: "ASC"
            },
          },
          {
            text: "Email",
            value: "email",
            sortable: {
              key: "email",
              order: "ASC"
            }
          },
          {
            text: "Status",
            value: "status",
            transform: (value: string) => {
              const parsedValue = value.replace(" ", "_").toLowerCase();
              return `<span class="statusPill statusPill--${parsedValue}">${value}</span>`;
            },
            align: "left",
            sortable: {
              key: "status",
              order: "ASC"
            }
          },
          {
            text: "Adres gecheckt",
            value: "address_checked",
            transform: (address_checked: boolean) => {
              if (address_checked) {
                return `<i aria-hidden="true" class="v-icon material-icons theme--light view" style="margin-left: 27px;">check</i>`;
              }

              return `<i aria-hidden="true" class="v-icon material-icons theme--light view" style="margin-left: 27px;">error</i>`;
            },
            sortable: {
              key: "address_checked",
              order: "ASC"
            }
          },
          {
            text: "Aangemaakt op",
            breakTitle: true,
            value: "created_at",
            transform: (date: string) => {
              return DateTime.fromSQL(date).toFormat("dd-LL-yyyy");
            },
            align: "right",
            class: 'text-right',
            sortable: {
              key: "created_at",
              order: "ASC"
            }
          },
          {
            text: "Sluitingsdatum",
            breakTitle: true,
            breakTitleArray: ["Sluitings", "datum"],
            value: "closed_at",
            transform: (date: string) => {
              if (date === "") {
                return "";
              } else {
                return DateTime.fromSQL(date).toFormat("dd-LL-yyyy");
              }
            },
            align: "right",
            class: 'text-right',
            sortable: {
              key: "closed_at",
              order: "ASC"
            }
          },
          {
            breakTitle: true,
            breakTitleArray: ["Bezorg", "datum"],
            text: "Bezorgdatum",
            value: "delivery_at",
            transform: (date: string) => {
              return date ? DateTime.fromSQL(date).toFormat("dd-LL-yyyy") : "";
            },
            align: "right",
            class: 'text-right',
          },
          {
            text: "Drop off",
            breakTitle: true,
            value: "last_answered_question",
            transform: (value: string) => {
              const lastQuestion = this.questions.find((question: Question) => {
                return question.key === value;
              });

              // 1 is the start form which is not countend
              const questionsLength = this.questions.length - 1;

              if (lastQuestion) {
                lastQuestion["last_answered_question"] = `${this.questions.indexOf(lastQuestion as Question) > 0
                  ? this.questions.indexOf(lastQuestion as Question)
                  : "0"
                  }/${questionsLength}`;
              }

              return `<span style="cursor: pointer;">${this.questions.indexOf(lastQuestion as Question) > 0
                ? this.questions.indexOf(lastQuestion as Question)
                : "0"
                }/${questionsLength}</span>`;
            },
            align: "right",
            class: 'text-right',
            action: (lead: Lead) => {
              const lastQuestion = this.questions.find((question: Question) => {
                return question.key === lead.last_answered_question;
              });

              this.lastQuestion = lastQuestion as Question;

              if (this.lastQuestion) {
                this.$store.dispatch(
                  "openDialog",
                  this.dialogOptionsLastQuestion
                );
              }
            }
          }
        ],
        filters: [],
        actions: [
          {
            type: "view",
            label: "view",
            icon: "remove_red_eye",
            action: "/leads/{id}"
          }
        ],
        dropdown: [],
        model: new Lead().filter(this.leadFilterObject)
      },
      Contacts: {
        name: {
          singular: this.$tc('contact'),
          plural: this.$tc('contacts')
        },
        sort: {
          key: "name",
          order: "ASC"
        },
        headers: [
          {
            text: this.$tc('tableHeaders.name.title'),
            value: "display_name",
            action: "/contacten/{id}",
            sortable: {
              key: "name",
              order: "ASC"
            }
          },
          {
            text: this.$tc('tableHeaders.email.title'),
            value: "email",
            sortable: {
              key: "email",
              order: "ASC"
            }
          },
          {
            text: this.$tc('tableHeaders.status.title'),
            value: "profile",
            transform: (profile: string) => {
              // @ts-ignore
              if (!profile || !profile["status"]) {
                return '-';
              }

              // @ts-ignore
              const value = profile["status"];
              const capitalizedStatus = value.charAt(0).toUpperCase() + value.slice(1);
              return `<span class="statusPill statusPill--${value}">${capitalizedStatus}</span>`;
            },
          },
          ...(!this.$store.state.Auth || !this.$store.state.Auth.hasRole([RoleEnum.STYLIST])
            ? [
              {
                text: this.$tc('tableHeaders.adressChecked.title'),
                value: 'profile',
                // @ts-ignore
                transform: (profile) => {
                  if (!profile) {
                    return '';
                  }

                  const value = profile['address_checked'];
                  if (value) {
                    return `<i aria-hidden="true" class="v-icon material-icons theme--light view" style="margin-left: 27px;">check</i>`;
                  }
                  return `<i aria-hidden="true" class="v-icon material-icons theme--light view" style="margin-left: 27px;">error</i>`;
                },
              },
            ]
            : []),
          {
            text: this.$tc('tableHeaders.city.title'),
            value: "city",
          },
          {
            text: this.$tc('tableHeaders.age.title'),
            value: "age",
          },
          {
            text: this.$tc('tableHeaders.number.title'),
            value: "phone",
          },
          {
            text: this.$tc('languages.title'),
            value: "language",
            transform: (value: string) => {

              if (!value) {
                return '-'
              }

              const language = languageOptions.find(option => option.value === value);

              if (!language) {
                return '-'
              }
              return `<span>${language.country_code}</span>`;
            },
          },
          {
            text: this.$tc('tableHeaders.userID.title'),
            value: "user_id",
          },
          {
            text: this.$tc('tableHeaders.created_at.title'),
            value: "created_at",
          }
        ],
        actions: [
          {
            type: "view",
            label: "view",
            icon: "remove_red_eye",
            action: "/contacten/{id}"
          },
          {
            type: "delete",
            label: "delete",
            icon: "delete",
            action: (vm: Users, user: User) => {
              this.selectedUser = user;
              this.$store.dispatch("openDialog", this.dialogOptions);
            }
          }
        ],
        add: () => {
          this.isCreating = true;
        },
        dropdown: [],
        model: new Contact().include(['profile'])
      },
      Suppliers: {
        name: {
          singular: "Leverancier",
          plural: "Leveranciers"
        },
        sort: {
          key: "name",
          order: "ASC"
        },
        headers: [
          {
            text: "Leveranciers",
            value: "name",
            action: "/leveranciers/{id}",
            sortable: {
              key: "name",
              order: "ASC"
            }
          },
          {
            text: "Shortcode",
            value: "short_name",
            sortable: {
              key: "short_name",
              order: "ASC"
            }
          }
        ],
        actions: [
          {
            type: "view",
            label: "view",
            icon: "remove_red_eye",
            action: "/leveranciers/{id}"
          },
          {
            type: "delete",
            label: "delete",
            icon: "delete",
            action: (vm: Users, user: User) => {
              this.selectedUser = user;
              this.$store.dispatch("openDialog", this.dialogOptions);
            }
          }
        ],
        add: () => {
          this.isCreatingSupplier = true;
        },
        dropdown: [],
        model: new Supplier()
      },
      Stylists: {
        name: {
          singular: "Stylist",
          plural: "Stylisten"
        },
        sort: {
          key: "name",
          order: "ASC"
        },
        headers: [
          {
            text: "Naam",
            value: "display_name",
            action: "/stylisten/{id}",
            sortable: {
              key: "name",
              order: "ASC"
            }
          },
          {
            text: "Email",
            value: "email",
            sortable: {
              key: "email",
              order: "ASC"
            }
          },
          {
            text: this.$tc('languages.title'),
            value: "language",
            transform: (value: string) => {

              if (!value) {
                return '-'
              }

              const language = languageOptions.find(option => option.value === value);

              if (!language) {
                return '-'
              }
              return `<span>${language.country_code}</span>`;
            },
          },
          {
            text: "Is planner",
            value: "is_planner",
            transform: (value: boolean) => {
              return value ? 'Ja' : 'Nee';
            }
          },
          {
            text: "MFA ingesteld",
            value: "has_mfa",
            transform: (value: boolean) => {
              return value ? 'Ja' : 'Nee';
            }
          },
          {
            text: "Is actief",
            value: "is_active",
            transform: (value: boolean) => {
              return value ? 'Ja' : 'Nee';
            }
          },
        ],
        actions: [
          {
            type: "view",
            label: "view",
            icon: "remove_red_eye",
            action: "/stylisten/{id}"
          },
          {
            type: "view",
            label: "view",
            icon: "toggle_on",
            action: (vm: Users, user: User) => {
              this.selectedUser = user;
              this.$store.dispatch("openDialog", this.deactivateOptions);
            }
          },
          {
            type: "delete",
            label: "delete",
            icon: "delete",
            action: (vm: Users, user: User) => {
              this.selectedUser = user;
              this.$store.dispatch("openDialog", this.dialogOptions);
            }
          }
        ],
        add: () => {
          this.isCreating = true;
        },
        dropdown: [],
        model: new Stylist()
      },
      Admin: {
        name: {
          singular: "Admin",
          plural: "Admin"
        },
        sort: {
          key: "name",
          order: "ASC"
        },
        headers: [
          {
            text: "Naam",
            value: "display_name",
            action: "/admin/{id}",
            sortable: {
              key: "name",
              order: "ASC"
            }
          },
          {
            text: "Email",
            value: "email",
            sortable: {
              key: "email",
              order: "ASC"
            }
          },
          {
            text: this.$tc('languages.title'),
            value: "language",
            transform: (value: string) => {

              if (!value) {
                return '-'
              }

              const language = languageOptions.find(option => option.value === value);

              if (!language) {
                return '-'
              }
              return `<span>${language.country_code}</span>`;
            },
          },
          {
            text: "MFA ingesteld",
            value: "has_mfa",
            transform: (value: boolean) => {
              return value ? 'Ja' : 'Nee';
            }
          },
        ],
        actions: [
          {
            type: "view",
            label: "view",
            icon: "remove_red_eye",
            action: "/admin/{id}"
          },
          {
            type: "delete",
            label: "delete",
            icon: "delete",
            action: (vm: Users, user: User) => {
              this.selectedUser = user;
              this.$store.dispatch("openDialog", this.dialogOptions);
            }
          }
        ],
        add: () => {
          this.isCreating = true;
        },
        dropdown: [],
        model: new Admin()
      },
      Support: {
        name: {
          singular: "Support",
          plural: "Support"
        },
        sort: {
          key: "name",
          order: "ASC"
        },
        headers: [
          {
            text: "Naam",
            value: "display_name",
            action: "/support/{id}",
            sortable: {
              key: "name",
              order: "ASC"
            }
          },
          {
            text: "Email",
            value: "email",
            sortable: {
              key: "email",
              order: "ASC"
            }
          },
          {
            text: this.$tc('languages.title'),
            value: "language",
            transform: (value: string) => {

              if (!value) {
                return '-'
              }

              const language = languageOptions.find(option => option.value === value);

              if (!language) {
                return '-'
              }
              return `<span>${language.country_code}</span>`;
            },
          },
          {
            text: "MFA ingesteld",
            value: "has_mfa",
            transform: (value: boolean) => {
              return value ? 'Ja' : 'Nee';
            }
          },
        ],
        actions: [
          {
            type: "view",
            label: "view",
            icon: "remove_red_eye",
            action: "/support/{id}"
          },
          {
            type: "delete",
            label: "delete",
            icon: "delete",
            action: (vm: Users, user: User) => {
              this.selectedUser = user;
              this.$store.dispatch("openDialog", this.dialogOptions);
            }
          }
        ],
        add: () => {
          this.isCreating = true;
        },
        dropdown: [],
        model: new Support()
      }
    };
    return sourceMap[this.type as string];
  }

  get dialogOptionsMany(): Options {
    return {
      title: this.$t("dialogOptions.confirmation").toString(),
      text: `${this.$t("dialogOptions.deleteMany", {
        item: this.selected.length + " " + this.$tc("roles.user", 2)
      }).toString()}`,
      type: "warning",
      buttons: {
        confirm: {
          text: this.$t("dialogOptions.button.delete").toString(),
          action: () => {
            if (this.selected.length <= 0) {
              return;
            }

            let counter = 0;
            this.selected.forEach((user: User) => {
              user
                .delete()
                .then((response: any) => {
                  if (response) {
                    counter += 1;

                    if (counter >= this.selected.length) {
                      this.selected = [];
                      this.userDatatable ? this.userDatatable.refresh() : null;
                    }
                  }
                })
                .catch((error: AxiosError) => {
                  ErrorHandler.network(error);
                });
            });
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.cancel").toString(),
          action: () => { }
        }
      }
    };
  }

  get dialogOptions(): Options {
    const user: User = this.selectedUser as User;

    return {
      title:
        (user.name ? user.name : "") ||
        this.$t("dialogOptions.confirmation").toString(),
      text: this.$tc("dialogOptions.delete", 2, {
        item: this.$t("dialogOptions.item.user")
      }).toString(),
      type: "warning",
      buttons: {
        confirm: {
          text: this.$t("dialogOptions.button.delete").toString(),
          action: () => {
            if (!user) {
              return;
            }

            user
              .delete()
              .then((response: any) => {
                if (response) {
                  this.selectedUser = null;
                  this.userDatatable ? this.userDatatable.refresh() : null;
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.cancel").toString(),
          action: () => {
            this.selectedUser = null;
          }
        }
      }
    };
  }

  get deactivateOptions(): Options {
    const user: User = this.selectedUser as User;

    return {
      title:
        this.$t("dialogOptions.confirmation").toString(),
      text: `Weet je zeker dat je deze stylist wilt ${user.is_active ? 'deactiveren' : 'activeren'}?`,
      type: "warning",
      buttons: {
        confirm: {
          text: `${user.is_active ? 'deactiveren' : 'activeren'}`,
          action: () => {
            if (!user) {
              return;
            }

            user
              .toggleIsActive()
              .then((response: any) => {
                if (response) {
                  if (this.selectedUser) {
                    this.selectedUser.is_active = !this.selectedUser.is_active;
                  }
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.cancel").toString(),
          action: () => {
            this.selectedUser = null;
          }
        }
      }
    };
  }

  public userCreated() {
    this.isCreating = false;
    this.userDatatable ? this.userDatatable.refresh() : null;
  }

  @Watch("selected")
  public selectedItemsChange(value: User[], oldValue: User[]) { }

  get user() {
    return (
      this.$store.state.Auth ||
      new User({
        name: "Undefined"
      })
    );
  }

  protected get userDatatable(): DataTable | null {
    const dataTable = this.$refs.userDatatable;
    if (dataTable) {
      return dataTable as DataTable;
    }

    return null;
  }

  get dialogOptionsLastQuestion(): Options {
    return {
      text: `<div style="text-align: left;">Vraagnummer: ${this.lastQuestion ? this.lastQuestion.last_answered_question : ""
        } <br> Category: ${this.lastQuestion ? this.lastQuestion.category : ""
        } <br> Vraag: ${this.lastQuestion ? this.lastQuestion.questionText : ""
        }</div>`,
      buttons: {
        confirm: {
          text: this.$t("dialogOptions.button.ok").toString(),
          action: () => {
            this.lastQuestion = null;
          }
        }
      }
    };
  }
}

interface SourceMap {
  [key: string]: SourceMapItem;
}

interface SourceMapItem {
  headers: TableHeader;
  actions: any[];
  model: Model;
}

export interface TableVisibility {
  title?: boolean;
  search?: boolean;
  total?: boolean;
  limit?: boolean;
  pagination?: boolean;
  checkboxes?: boolean;
}

export interface customFilter {
  status?: string;
  type?: string;
  items?: any[];
}

export interface Question {
  last_answered_question?: string;
  type?: string;
  category?: string;
  title?: string;
  questionText?: string;
  key?: string;
  images?: any;
  reviews?: Review[];
  image?: any;
  count?: string;
  next_category?: string;
  text?: string;
  toolTip?: string;
  labelTop?: string;
  labelBottom?: string;
  description?: string;
  maxSelection?: number;
  showCount?: boolean;
  checkboxes?: Checkboxes[];
  pills?: Pills[];
  inputs?: Inputs[];
  radiobuttons?: Radiobuttons[];
}

interface Checkboxes {
  label?: string;
  key?: string;
}

interface Pills {
  title?: string;
  radiobuttons?: Radiobuttons[];
  key?: string;
}

interface Radiobuttons {
  label?: string;
  key?: string;
}

interface Inputs {
  label?: string;
  key?: string;
  type?: string;
  placeholder?: string;
  options?: string[];
  alert?: string;
  toolTip?: string;
}

interface Review {
  name?: string;
  description?: string;
  image?: any;
}
